<script setup lang="ts">
import { debounce } from 'lodash-es';
import { computed, ref } from 'vue';
import { useQualificationsQuery } from '/@entities/qualification/api/useQualificationsQuery';
import { useUserStore } from '/@stores/user';
import { Resource } from '/@types/resources';
import { RolePermission } from '/@types/user';

const userStore = useUserStore();

const { resources } = defineProps<{ resources: Resource[] }>();

const selectedIds = defineModel<Set<number>>({ required: true });

const searchFilter = ref('');

const userHasQualificationsPermission = computed(() =>
  userStore.hasRolePerm(RolePermission.QualificationUser),
);

const defaultText = computed(() => {
  if (!userHasQualificationsPermission.value) return 'Du har ikke tilgang til å se kompetanser';

  if (!resourcesQualifications.value || resourcesQualifications.value.size === 0)
    return 'Ingen kompetanser lagret på utvalgte ressurser';

  return 'Alle';
});

const resourcesQualifications = computed(() => {
  let ids = new Set();

  resources.forEach((resource) => {
    if (resource.type === 'user') {
      resource.qualifications?.forEach((x) => ids.add(x.id));
    } else {
      const subResources = resource.subresources;
      subResources.forEach((sub) => {
        sub.qualifications?.forEach((x) => ids.add(x.id));
      });
    }
  });

  return ids;
});

const filteredQualifications = computed(() =>
  qualifications.value
    ? [...qualifications.value.values()]
        .filter(
          (x) =>
            x.Name.toLocaleLowerCase().includes(searchFilter.value.toLocaleLowerCase()) &&
            x.IsAvailableInResource &&
            [...resourcesQualifications.value].includes(x.Id),
        )
        .sort((a, b) => a.Name.localeCompare(b.Name, 'nb-no'))
    : [],
);

const activeFilterText = computed(() => {
  const values = selectedIds.value;
  const firstId = values?.values().next().value;
  const found = qualifications.value?.find((x) => x.Id === firstId);

  if (values && found) {
    return values.size > 1 ? `${found.Name} +${values.size - 1}` : found.Name;
  } else {
    return null;
  }
});

const { data: qualifications } = useQualificationsQuery();

const onSearchChanged = debounce(
  (value?: string | null) => (searchFilter.value = value ?? ''),
  200,
);

function toggleFilterValue(value: number) {
  return selectedIds.value?.has(value)
    ? selectedIds.value.delete(value)
    : selectedIds.value?.add(value);
}
</script>

<template>
  <df-select class="" label="Kompetanser" elevate :active="selectedIds && selectedIds.size > 0">
    <template v-slot:text>
      {{ activeFilterText ?? 'Velg kompetanse' }}
    </template>

    <df-input-search
      v-if="qualifications && qualifications.length > 8"
      :model-value="searchFilter"
      v-on:input="onSearchChanged"
      placeholder="Søk"
      dropdown-item
      autofocus
    />

    <df-dropdown2-item v-on:click="selectedIds?.clear()"> {{ defaultText }} </df-dropdown2-item>

    <df-dropdown2-item
      v-for="filter in filteredQualifications"
      :key="filter.Id"
      v-on:click="toggleFilterValue(filter.Id)"
      no-close
    >
      {{ filter.Name }}
      <template v-slot:icon>
        <df-icon :code="selectedIds?.has(filter.Id) ? 'f14a' : 'f0c8'" />
      </template>
    </df-dropdown2-item>
  </df-select>
</template>

<style scoped></style>
